/**
 * Booking confirmation codes.
 *
 * Format: 8 characters, uppercase A-Z0-9. Ambiguous characters (I, O, 0, 1)
 * are excluded so codes can be read out loud and typed without mistakes.
 *
 * Older rows may still hold lowercase hexadecimal codes, so always render them
 * through `formatConfirmationCode` and look them up case-insensitively with
 * `confirmationCodeWhere`.
 */
const CONFIRMATION_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

export const CONFIRMATION_CODE_LENGTH = 8;

/** Generates a new confirmation code: 8 characters, uppercase A-Z0-9. */
export function generateConfirmationCode(): string {
  const bytes = new Uint8Array(CONFIRMATION_CODE_LENGTH);
  const webCrypto: Crypto | undefined = (globalThis as any)?.crypto;

  if (webCrypto?.getRandomValues) {
    webCrypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  let code = "";
  // 256 is a multiple of 32, so the modulo introduces no bias.
  for (let i = 0; i < CONFIRMATION_CODE_LENGTH; i += 1) {
    code += CONFIRMATION_CODE_ALPHABET[bytes[i] % CONFIRMATION_CODE_ALPHABET.length];
  }
  return code;
}

/** Renders a confirmation code for display. Old lowercase codes are uppercased. */
export function formatConfirmationCode(
  code: string | null | undefined
): string {
  return (code ?? "").toUpperCase();
}

/**
 * Prisma filter that matches a stored confirmation code regardless of case,
 * so codes created before the format was unified keep working.
 */
export function confirmationCodeWhere(code: string) {
  return { equals: code, mode: "insensitive" as const };
}
